import { useMemo } from "react";
import { useFlightRestrictionZonesQueryV1 } from "@/api/v1/flight-restriction-zone-api";
import type { FlightRestrictionZoneResponse } from "@/api/v1/flight-restriction-zone-api";
import {  matchesAltitudeRange, apiTypeToLabel } from "@/lib/zone-type-utils";
import { FlightRestrictionZonesTable } from "@/components/flight-restriction-zones/table/data-table";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { BreadcrumbNav } from "@/components/ui/breadcrumb-nav";
import { 
  AlertTriangle, 
  Shield, 
  AlertCircle, 
  MapPin, 
  RefreshCw
} from "lucide-react";
import { Skeleton } from "@/components/ui/skeleton";
import { useQueryState, parseAsString, parseAsStringEnum, parseAsInteger } from "nuqs";
import { useStore } from "@/store/store";

export const FlightRestrictionZonesRoute = () => {
  const [typeFilter, setTypeFilter] = useQueryState(
    "type", 
    parseAsStringEnum(["Prohibited", "Restricted", "Danger"])
  );
  const [statusFilter, setStatusFilter] = useQueryState(
    "status", 
    parseAsStringEnum(["active", "inactive"])
  );
  const [search, setSearch] = useQueryState("search", parseAsString.withDefault(""));
  const [page, setPage] = useQueryState("page", parseAsInteger.withDefault(1));
  const [pageSize, setPageSize] = useQueryState("pageSize", parseAsInteger.withDefault(10));
  
  // Get altitude range filter from store
  const minAltitude = useStore((state) => state.minAltitude);
  const maxAltitude = useStore((state) => state.maxAltitude);

  const { 
    data: zones, 
    isLoading, 
    error,
    refetch 
  } = useFlightRestrictionZonesQueryV1();

  // Filter zones client-side (v1 API returns all zones, no server-side filtering/pagination)
  const filteredZones: FlightRestrictionZoneResponse[] = useMemo(() => {
    if (!zones) return [];
    
    return zones.filter(zone => {
      // Type filter - convert UI label to API code for comparison
      if (typeFilter) {
        const zoneLabel = apiTypeToLabel(zone.type);
        if (zoneLabel !== typeFilter) return false;
      }
      
      // Status filter - check if zone is currently active based on effective times
      if (statusFilter) {
        const now = new Date();
        const beginTime = new Date(zone.effective_begin_time);
        const endTime = zone.effective_end_time ? new Date(zone.effective_end_time) : null;
        const isActive = beginTime <= now && (!endTime || endTime >= now);
        
        if (statusFilter === "active" && !isActive) return false;
        if (statusFilter === "inactive" && isActive) return false;
      }
      
      // Search filter
      if (search) {
        const searchLower = search.toLowerCase();
        if (!(
          zone.name.toLowerCase().includes(searchLower) ||
          zone.designator.toLowerCase().includes(searchLower) ||
          zone.identifier.toLowerCase().includes(searchLower)
        )) return false;
      }
      
      // Altitude range filter (check if zone overlaps with operating altitude)
      if (!matchesAltitudeRange(
        zone.upper_limit,
        zone.lower_limit,
        minAltitude,
        maxAltitude
      )) return false;
      
      return true;
    });
  }, [zones, typeFilter, statusFilter, search, minAltitude, maxAltitude]);

  // Paginate client-side
  const paginatedZones = useMemo(() => {
    const start = (page - 1) * pageSize;
    const end = start + pageSize;
    return filteredZones.slice(start, end);
  }, [filteredZones, page, pageSize]);

  // Calculate statistics from filtered zones
  const stats = useMemo(() => {
    const now = new Date();
    return {
      total: filteredZones.length,
      active: filteredZones.filter(z => {
        const beginTime = new Date(z.effective_begin_time);
        const endTime = z.effective_end_time ? new Date(z.effective_end_time) : null;
        return beginTime <= now && (!endTime || endTime >= now);
      }).length,
      prohibited: filteredZones.filter(z => apiTypeToLabel(z.type) === "Prohibited").length,
      restricted: filteredZones.filter(z => apiTypeToLabel(z.type) === "Restricted").length,
      danger: filteredZones.filter(z => apiTypeToLabel(z.type) === "Danger").length,
    };
  }, [filteredZones]);

  const handleRefresh = () => {
    refetch();
  };

  const handleTypeFilter = (type: string | undefined) => {
    setTypeFilter(type as "Prohibited" | "Restricted" | "Danger" | null);
    setPage(1); // Reset to first page when changing type filter
  };

  const handleStatusFilter = (status: string | undefined) => {
    setStatusFilter(status as "active" | "inactive" | null);
    setPage(1); // Reset to first page when changing status filter
  };

  const handlePageChange = (newPage: number) => {
    const totalPages = Math.ceil(filteredZones.length / pageSize);
    if (newPage >= 1 && newPage <= totalPages) {
      setPage(newPage);
    }
  };

  const handlePageSizeChange = (newPageSize: number) => {
    setPageSize(newPageSize);
    setPage(1); // Reset to first page when changing page size
  };

  const handleSearchChange = (newSearch: string) => {
    setSearch(newSearch);
    setPage(1); // Reset to first page when searching
  };

  if (error) {
    return (
      <main>
        <div className="container mx-auto p-8 space-y-6">
          <BreadcrumbNav
            items={[
              { label: "Home", href: "/" },
              { label: "Flight Restrictions", href: "/flight-restriction-zones" },
            ]}
            className="mb-6"
          />
          <div className="flex items-center justify-between">
            <h1 className="text-2xl font-semibold">Flight Restriction Zones</h1>
          </div>
          <Card>
            <CardContent className="pt-6">
              <div className="flex flex-col items-center justify-center py-12 text-center">
                <AlertCircle className="h-12 w-12 text-destructive mb-4" />
                <h3 className="text-lg font-semibold mb-2">Failed to load zones</h3>
                <p className="text-muted-foreground mb-4">
                  There was an error loading the flight restriction zones. Please try again.
                </p>
                <Button onClick={handleRefresh}>
                  <RefreshCw className="h-4 w-4 mr-2" />
                  Try Again
                </Button>
              </div>
            </CardContent>
          </Card>
        </div>
      </main>
    );
  }

  return (
    <main>
      <div className="container mx-auto p-4 sm:p-6 lg:p-8 space-y-4 sm:space-y-6">
        <BreadcrumbNav
          items={[
            { label: "Home", href: "/" },
            { label: "Flight Restrictions", href: "/flight-restriction-zones" },
          ]}
          className="mb-6"
        />
        {/* Header */}
        <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-2">
          <h1 className="text-xl sm:text-2xl font-semibold">Flight Restriction Zones</h1>
          <div className="flex gap-2">
            <Button variant="outline" onClick={handleRefresh} disabled={isLoading} size="sm" className="text-xs sm:text-sm">
              <RefreshCw className={`h-3 w-3 sm:h-4 sm:w-4 mr-1 sm:mr-2 ${isLoading ? 'animate-spin' : ''}`} />
              Refresh
            </Button>
          </div>
        </div>

        {/* Statistics Cards */}
        <div className="grid gap-3 sm:gap-4 grid-cols-2 lg:grid-cols-5">
          <Card>
            <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-1 sm:pb-2">
              <CardTitle className="text-xs sm:text-sm font-medium">Total Zones</CardTitle>
              <MapPin className="h-3 w-3 sm:h-4 sm:w-4 text-muted-foreground" />
            </CardHeader>
            <CardContent className="pt-2 sm:pt-0">
              <div className="text-lg sm:text-2xl font-bold">
                {isLoading ? <Skeleton className="h-6 sm:h-8 w-12 sm:w-16" /> : stats.total}
              </div>
              <p className="text-xs text-muted-foreground">
                All restriction zones
              </p>
            </CardContent>
          </Card>

          <Card>
            <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-1 sm:pb-2">
              <CardTitle className="text-xs sm:text-sm font-medium">Active Zones</CardTitle>
              <Shield className="h-3 w-3 sm:h-4 sm:w-4 text-green-600" />
            </CardHeader>
            <CardContent className="pt-2 sm:pt-0">
              <div className="text-lg sm:text-2xl font-bold text-green-600">
                {isLoading ? <Skeleton className="h-6 sm:h-8 w-12 sm:w-16" /> : stats.active}
              </div>
              <p className="text-xs text-muted-foreground">
                Currently enforced
              </p>
            </CardContent>
          </Card>

          <Card>
            <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-1 sm:pb-2">
              <CardTitle className="text-xs sm:text-sm font-medium">Prohibited</CardTitle>
              <AlertTriangle className="h-3 w-3 sm:h-4 sm:w-4 text-red-600" />
            </CardHeader>
            <CardContent className="pt-2 sm:pt-0">
              <div className="text-lg sm:text-2xl font-bold text-red-600">
                {isLoading ? <Skeleton className="h-6 sm:h-8 w-12 sm:w-16" /> : stats.prohibited}
              </div>
              <p className="text-xs text-muted-foreground">
                No-fly zones
              </p>
            </CardContent>
          </Card>

          <Card>
            <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-1 sm:pb-2">
              <CardTitle className="text-xs sm:text-sm font-medium">Restricted</CardTitle>
              <AlertCircle className="h-3 w-3 sm:h-4 sm:w-4 text-red-600 dark:text-red-400" />
            </CardHeader>
            <CardContent className="pt-2 sm:pt-0">
              <div className="text-lg sm:text-2xl font-bold text-red-600 dark:text-red-400">
                {isLoading ? <Skeleton className="h-6 sm:h-8 w-12 sm:w-16" /> : stats.restricted}
              </div>
              <p className="text-xs text-muted-foreground">
                Limited access
              </p>
            </CardContent>
          </Card>

          <Card>
            <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-1 sm:pb-2">
              <CardTitle className="text-xs sm:text-sm font-medium">Danger</CardTitle>
              <AlertTriangle className="h-3 w-3 sm:h-4 sm:w-4 text-orange-600 dark:text-orange-400" />
            </CardHeader>
            <CardContent className="pt-2 sm:pt-0">
              <div className="text-lg sm:text-2xl font-bold text-orange-600 dark:text-orange-400">
                {isLoading ? <Skeleton className="h-6 sm:h-8 w-12 sm:w-16" /> : stats.danger}
              </div>
              <p className="text-xs text-muted-foreground">
                High risk areas
              </p>
            </CardContent>
          </Card>
        </div>

        {/* Main Content */}
        <Card>
          <CardHeader>
            <CardTitle>Flight Restriction Zones</CardTitle>
            <CardDescription>
              View and manage all flight restriction zones in your area. 
              Use filters to narrow down results by type, status, or location.
            </CardDescription>
          </CardHeader>
          <CardContent>
            <FlightRestrictionZonesTable
              data={paginatedZones}
              isLoading={isLoading}
              onRefresh={handleRefresh}
              onTypeFilter={handleTypeFilter}
              onStatusFilter={handleStatusFilter}
              search={search}
              onSearchChange={handleSearchChange}
              // Client-side pagination props
              totalCount={filteredZones.length}
              currentPage={page}
              pageSize={pageSize}
              onPageChange={handlePageChange}
              onPageSizeChange={handlePageSizeChange}
            />
          </CardContent>
        </Card>
      </div>
    </main>
  );
};
